import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import UITextField from "@/components/ui-controls/textfield";
import GetIcon from "@/components/font-awesome-icon";

const getRowKey = (row) => row?._rowId ?? row?.id ?? "";

const DynamicFieldWrapper = ({ form, title = "", fieldKey = "" }) => {
  const rowIdRef = useRef(0);
  const nextRowId = () => {
    rowIdRef.current += 1;
    return rowIdRef.current;
  };

  useEffect(() => {
    const rows = form.values[fieldKey] || [];
    if (!rows.length) {
      return;
    }
    if (rows.some((row) => row._rowId == null)) {
      form.setFieldValue(
        fieldKey,
        rows.map((row) => ({
          ...row,
          _rowId: row._rowId ?? row.id ?? nextRowId(),
        })),
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyOptionRows = (updatedFields) => {
    form.setFieldValue(fieldKey, updatedFields, false);
    form.setTouched(
      { ...form.touched, [fieldKey]: updatedFields.map(() => false) },
      false
    );
    form.setFieldError(fieldKey, undefined);
  };

  const addField = () => {
    applyOptionRows([
      ...form.values[fieldKey],
      { value: "", _rowId: nextRowId() },
    ]);
  };

  const onFieldChange = (index, value) => {
    const updatedFields = [...form.values[fieldKey]];
    updatedFields[index] = { ...updatedFields[index], value };
    form.setFieldValue(fieldKey, updatedFields);
    form.setFieldTouched(`${fieldKey}[${index}]`, true, false);
  };

  const deleteField = (index) => {
    const updatedFields = [...form.values[fieldKey]];
    updatedFields.splice(index, 1);
    if (!updatedFields.length) {
      updatedFields.push({ value: "", _rowId: nextRowId() });
    }
    applyOptionRows(updatedFields);
  };

  const getError = (index) => {
    const rowTouched = form.touched[fieldKey]?.[index];
    if (!rowTouched) {
      return false;
    }
    const fieldErrors = form.errors[fieldKey];
    if (!fieldErrors) {
      return false;
    }
    const rowError = fieldErrors[index];
    if (typeof rowError === "string") {
      return rowError;
    }
    return rowError?.value || false;
  };

  return (
    <div>
      {form.values[fieldKey].map((field, index) => (
        <Box key={getRowKey(field) || `option-row-${index}`} sx={{ mb: 3 }}>
          <UITextField
            type={"text"}
            name={fieldKey}
            value={field.value}
            title={title}
            placeholder={title}
            onChange={(e) => onFieldChange(index, e.target.value)}
            postfix={
              form.values[fieldKey].length > 1 && (
                <GetIcon icon={faTrash} onClick={() => deleteField(index)} />
              )
            }
            error={getError(index)}
          />
        </Box>
      ))}

      <Typography
        variant="body2"
        sx={{ cursor: "pointer", color: "#0043CE", display: "inline-block" }}
        onClick={addField}
      >
        + add one more
      </Typography>
    </div>
  );
};

export default DynamicFieldWrapper;
